import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-bold transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stroke-blue disabled:pointer-events-none disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0 cursor-pointer aria-invalid:ring-2 aria-invalid:ring-stroke-critical aria-invalid:border-stroke-critical",
  {
    variants: {
      variant: {
        primary:
          "rounded-full bg-bg-brand-blue text-icon-inverse hover:bg-bg-button-active active:bg-bg-button-active disabled:bg-bg-disabled disabled:text-icon-disabled",
        secondary:
          "rounded-full border-2 bg-bg-primary border-stroke-blue text-icon-blue hover:border-stroke-button-hover hover:text-icon-hover active:border-stroke-button-hover active:text-icon-hover disabled:bg-bg-disabled disabled:border-stroke-secondary disabled:text-icon-disabled",
        tertiary:
          "rounded-full bg-transparent text-text-primary hover:bg-bg-hover hover:text-[#0000c9] active:bg-bg-active active:text-[#0000c9] disabled:bg-bg-disabled disabled:text-icon-disabled",
        "tertiary-outlined":
          "rounded-full bg-transparent text-text-primary border border-stroke-secondary hover:bg-bg-hover hover:text-[#0000c9] active:bg-bg-active active:text-[#0000c9] disabled:bg-bg-disabled disabled:text-icon-disabled disabled:border-stroke-secondary",
        ghost:
          "rounded-full bg-transparent text-text-primary hover:bg-bg-hover hover:text-[#0000c9] active:bg-bg-active active:text-[#0000c9] disabled:bg-bg-disabled disabled:text-icon-disabled",
        round:
          "rounded-full bg-bg-brand-blue text-icon-inverse hover:bg-bg-button-active active:bg-bg-button-active disabled:bg-bg-disabled disabled:text-icon-disabled",
        circle:
          "rounded-full bg-transparent text-text-primary hover:bg-bg-hover active:bg-bg-active disabled:bg-bg-disabled disabled:text-icon-disabled disabled:opacity-50",
        "circle-critical":
          "rounded-full bg-bg-critical text-text-primary hover:bg-bg-critical/80 active:bg-bg-critical/70 disabled:bg-bg-disabled disabled:text-icon-disabled disabled:opacity-50",
        "circle-warning":
          "rounded-full bg-bg-warning text-text-primary hover:bg-bg-warning/80 active:bg-bg-warning/70 disabled:bg-bg-disabled disabled:text-icon-disabled disabled:opacity-50",
        "circle-success":
          "rounded-full bg-bg-success text-text-primary hover:bg-bg-success/80 active:bg-bg-success/70 disabled:bg-bg-disabled disabled:text-icon-disabled disabled:opacity-50",
        square:
          "rounded-lg bg-transparent text-text-primary hover:bg-bg-hover active:bg-bg-active disabled:bg-bg-disabled disabled:text-icon-disabled",
      },
      size: {
        large: "h-12 px-8 text-base [&_svg]:size-5",
        medium: "h-10 px-6 text-sm [&_svg]:size-4",
        small: "h-8 px-5 text-sm [&_svg]:size-4",
        "icon-large": "h-12 w-12 [&_svg]:size-6",
        "icon-medium": "h-10 w-10 [&_svg]:size-5",
        "icon-small": "h-8 w-8 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "large",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant = "primary",
  size = "large",
  asChild = false,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
}
Button.displayName = "Button";

export { Button, buttonVariants };
